import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import type {RefObject} from 'react'
import type React from 'react'
import {createContext, use, useMemo, useRef, useState} from 'react'

import {VALUES} from '../constants/values'
import type {DisplayMode} from '../utils/display-mode'
import type {SafeOptionConfig} from '../utils/option-config'

type IssueCreateMoreCreatedPath = {
  owner: string
  repo: string
  number: number | undefined
}

export type IssueCreateConfigProviderProps = {
  optionConfig: SafeOptionConfig
  overrideFallbackDisplaymode?: DisplayMode
  children: React.ReactNode
}

type IssueCreateOnCreateAction = {
  onCreate: (isSubmitting: boolean, createMore: boolean) => void
}

type IssueCreateConfigContextState = {
  createMore: boolean
  setCreateMore: (value: boolean) => void

  createMoreCreatedPath: IssueCreateMoreCreatedPath
  setCreateMoreCreatedPath: (value: IssueCreateMoreCreatedPath) => void

  isSubmitting: boolean
  setIsSubmitting: (value: boolean) => void

  isFileUploading: boolean
  setIsFileUploading: (value: boolean) => void

  displayMode: DisplayMode
  setDisplayMode: (value: DisplayMode) => void

  initialDefaultDisplayMode: DisplayMode

  onCreateAction: RefObject<IssueCreateOnCreateAction | null>

  optionConfig: SafeOptionConfig

  /** Indicates whether the issue we are creating is being added as a sub-issue */
  isSubIssue: boolean

  /** Indicates whether we are creating a sub-issue inline from a project table view */
  isCreatingSubIssueFromProject: boolean
}

const IssueCreateContext = createContext<IssueCreateConfigContextState | null>(null)

export function IssueCreateConfigContextProvider({
  optionConfig,
  overrideFallbackDisplaymode,
  children,
}: IssueCreateConfigProviderProps) {
  const {issueCreateMore} = VALUES.localStorageKeys

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isFileUploading, setIsFileUploading] = useState(false)
  const [createMore, setCreateMore] = useSessionStorage<boolean>(
    `config.${issueCreateMore(optionConfig.storageKeyPrefix)}`,
    false,
  )
  const [createMoreCreatedPath, setCreateMoreCreatedPath] = useState<IssueCreateMoreCreatedPath>({
    owner: '',
    repo: '',
    number: undefined,
  })

  const initialDefaultDisplayMode = optionConfig.getDefaultDisplayMode(overrideFallbackDisplaymode)
  const [displayMode, setDisplayMode] = useState(initialDefaultDisplayMode)

  const onCreateAction = useRef<IssueCreateOnCreateAction>(null)

  const contextValue: IssueCreateConfigContextState = useMemo(() => {
    return {
      optionConfig,
      createMore,
      setCreateMore,
      createMoreCreatedPath,
      setCreateMoreCreatedPath,
      displayMode,
      setDisplayMode,
      initialDefaultDisplayMode,
      isSubmitting,
      isFileUploading,
      setIsFileUploading,
      setIsSubmitting,
      onCreateAction,
      isSubIssue: !!optionConfig.issueCreateArguments?.parentIssue?.id,
      isCreatingSubIssueFromProject: optionConfig.issueCreateArguments?.isCreatingSubIssueFromProject ?? false,
    }
  }, [
    optionConfig,
    createMore,
    setCreateMore,
    createMoreCreatedPath,
    setCreateMoreCreatedPath,
    displayMode,
    initialDefaultDisplayMode,
    isSubmitting,
    isFileUploading,
  ])

  return <IssueCreateContext value={contextValue}>{children}</IssueCreateContext>
}

export const useIssueCreateConfigContext = () => {
  const context = use(IssueCreateContext)
  if (!context) {
    throw new Error('useIssueCreateConfigContext must be used within a IssueCreateConfigContextProvider.')
  }

  return context
}
